import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { apiBaseUrl } from './api/config'

export const fallbackPublicContent = {
  hero_eyebrow: 'Centro de servicio automotriz', hero_line_1: 'Tu máquina.', hero_line_2: 'En su mejor', hero_line_3: 'momento.',
  hero_support: 'Mantenimiento honesto, diagnóstico preciso y manos expertas para que disfrutes el camino.',
  hero_video_asset: '/assets/landing-video.mp4', hero_poster_asset: '/assets/hero-workshop.png',
  trust_headline: 'No solo reparamos autos.', trust_emphasis: 'Construimos confianza', trust_copy: 'En Lele’s Motors combinamos experiencia, atención cercana y precisión técnica. Te explicamos qué necesita tu vehículo, por qué y qué puedes esperar.',
  metrics: [{ value: 10, suffix: '+', label: 'Años de experiencia' }, { value: 360, suffix: '°', label: 'Revisión integral' }, { value: 100, suffix: '%', label: 'Compromiso' }],
  services: [{ n: '01', title: 'Mantenimiento', text: 'Servicio preventivo, cambio de aceite y revisión completa para mantener tu auto siempre listo.' }, { n: '02', title: 'Diagnóstico', text: 'Lectura precisa, revisión electrónica y respuestas claras antes de comenzar cualquier reparación.' }, { n: '03', title: 'Frenos', text: 'Inspección y reemplazo de componentes para recuperar una respuesta firme y segura.' }, { n: '04', title: 'Suspensión', text: 'Corrección de vibraciones, ruidos y desgaste para volver a disfrutar cada kilómetro.' }],
  experience_title: 'Claridad desde el diagnóstico hasta la entrega.', experience_points: ['Evaluación transparente', 'Repuestos de calidad', 'Seguimiento personalizado'], experience_image_asset: '/assets/taller.png', footer_cta: '¿Tu auto pide atención?', footer_value: 'Calidad · Confianza · Rendimiento', footer_year: 2026, social_image_asset: '/assets/hero-workshop.png',
}

const fallbackGallery = [
  { id: 'gallery-01', kind: 'photo', image_url: '/assets/gallery/Gallery-01.png', alt_text: "Equipo de Lele's Motors trabajando en un vehículo", caption: 'Trabajo en equipo' },
  { id: 'gallery-02', kind: 'photo', image_url: '/assets/gallery/Gallery-02.png', alt_text: "Especialista de Lele's Motors en el taller", caption: 'Experiencia automotriz' },
  { id: 'gallery-03', kind: 'panel', caption: 'Experiencia técnica' },
  { id: 'gallery-04', kind: 'photo', image_url: '/assets/gallery/Gallery-04.png', alt_text: 'Especialista realizando un trabajo de precisión', caption: 'Precisión en cada detalle' },
]

const fallbackSite = {
  configured: false,
  company: { name: 'Lele’s Motors', phone: '+50255950056', email: '', address: '23 Calle 39-63 · Ciudad de Guatemala', waze_url: 'https://waze.com/ul?ll=14.6240325%2C-90.4948152&navigate=yes&utm_source=leles_motors' },
  branch: { operating_hours: [{ weekday: 0, weekday_label: 'Lunes', opens_at: '08:00', closes_at: '17:30' }, { weekday: 5, weekday_label: 'Sábado', opens_at: '08:00', closes_at: '17:30' }] },
  content: fallbackPublicContent,
  gallery: fallbackGallery,
  promotions: [],
}

const PublicSiteContext = createContext(fallbackSite)

export function cleanPhone(phone) {
  return String(phone || '').replace(/[^+\d]/g, '')
}

export function displayPhone(phone) {
  const digits = cleanPhone(phone).replace(/^\+502/, '')
  return digits.length === 8 ? `${digits.slice(0, 4)} ${digits.slice(4)}` : phone
}

export function openingSummary(hours = []) {
  const open = hours.filter((item) => !item.is_closed && item.opens_at && item.closes_at)
  if (!open.length) return 'Consulta horarios'
  const first = open[0]; const last = open.at(-1)
  return `${first.weekday_label} — ${last.weekday_label}\n${first.opens_at} — ${first.closes_at}`
}

export function PublicSiteProvider({ children }) {
  const [site, setSite] = useState(fallbackSite)
  const [unavailable, setUnavailable] = useState(false)
  useEffect(() => {
    const controller = new AbortController()
    fetch(`${apiBaseUrl}/public/site/`, { headers: { Accept: 'application/json' }, signal: controller.signal })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('public-content-unavailable')))
      .then((payload) => { if (payload.configured && payload.content && payload.company) setSite(payload); else setUnavailable(true) })
      .catch((error) => { if (error.name !== 'AbortError') setUnavailable(true) })
    return () => controller.abort()
  }, [])
  const value = useMemo(() => ({ ...site, gallery: site.gallery?.length ? site.gallery : fallbackGallery, promotions: site.promotions || [], unavailable }), [site, unavailable])
  return <PublicSiteContext.Provider value={value}>{children}</PublicSiteContext.Provider>
}

export function usePublicSite() {
  return useContext(PublicSiteContext)
}
